import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ChevronRight,
  Loader2,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  AUDIENCE_WIZARD_BUTTON_CLASS,
  AUDIENCE_WIZARD_BUTTON_VARIANT,
} from '@/lib/constants/audience-wizard'
import { cn } from '@/lib/utils'
import {
  filterCleanColumns,
  toOperationalSet,
} from '@/layouts/AudienceImportSessionLayout/lib/operational-columns'
import { SegmentFilterQuestionPicker } from '@/components/audiences/SegmentFilterQuestionPicker'
import { buildSegmentFilterQuestions } from '@/lib/utils/segment-filter-questions'

import type { SegmentFilterColumnInput } from '@/lib/utils/segment-filter-questions'
import type { IdentitySourceOptions } from '@/types/components/audiences/import-session'

interface IdentityConfirmSourcePanelProps {
  sourceOptions: IdentitySourceOptions | null
  draftSourceColumn: string
  sourceSearch: string
  sourceDropdownOpen: boolean
  isPatching: boolean
  isLoadingMore?: boolean
  canSubmit: boolean
  hideButton?: boolean
  /** Overrides default "Map to question" (e.g. "Use this column" when remapping in map_values). */
  submitButtonLabel?: string
  /** Shown above the submit button when set (e.g. remap hint in map_values). */
  submitHint?: string | null
  readOnly?: boolean
  onDraftSourceColumnChange: (value: string) => void
  onSourceSearchChange: (value: string) => void
  onLoadMore: () => void
  onDropdownClose?: () => void
  onSourceDropdownOpenChange: (open: boolean) => void
  onSubmit: () => void
  onClear?: () => void
}

export function IdentityConfirmSourcePanel({
  sourceOptions,
  draftSourceColumn,
  isPatching,
  canSubmit,
  hideButton = false,
  submitButtonLabel,
  submitHint = null,
  readOnly = false,
  onDraftSourceColumnChange,
  onSourceSearchChange,
  onSourceDropdownOpenChange,
  onDropdownClose,
  onSubmit,
  onClear,
}: IdentityConfirmSourcePanelProps) {
  const { t } = useTranslation('app')
  const resolvedSubmitLabel = submitButtonLabel ?? t('audienceCreate.mapToQuestion')

  const rawCandidates = sourceOptions?.candidates ?? []
  const columnGroups = sourceOptions?.segmentFilterColumnGroups

  const operationalSet = useMemo(
    () => toOperationalSet(sourceOptions?.segmentFilterOperationalColumns),
    [sourceOptions?.segmentFilterOperationalColumns],
  )

  const pinnedColumns = useMemo(() => {
    const pins = new Set<string>()
    const current = sourceOptions?.currentSourceColumn
    if (current) pins.add(current)
    if (draftSourceColumn) pins.add(draftSourceColumn)
    return pins
  }, [sourceOptions?.currentSourceColumn, draftSourceColumn])

  const workspaceCandidates = useMemo(
    () => filterCleanColumns(rawCandidates, operationalSet, pinnedColumns),
    [rawCandidates, operationalSet, pinnedColumns],
  )

  const questions = useMemo(() => {
    const inputs: Array<SegmentFilterColumnInput> = workspaceCandidates.map((c) => ({
      sourceColumn: c.sourceColumn,
      normalizedLabel: c.label && c.label !== c.sourceColumn ? c.label : c.sourceColumn,
      originalLabel: c.label && c.label !== c.sourceColumn ? c.label : c.sourceColumn,
      category: null,
      sampleValues: [],
    }))
    return buildSegmentFilterQuestions(inputs, columnGroups ?? undefined)
  }, [workspaceCandidates, columnGroups])

  const displayLabel = useMemo(() => {
    if (!draftSourceColumn) return ''
    const cand = workspaceCandidates.find((c) => c.sourceColumn === draftSourceColumn)
    if (cand?.label && cand.label !== cand.sourceColumn) return cand.label
    return draftSourceColumn
  }, [draftSourceColumn, workspaceCandidates])

  const pickerValue = useMemo(() => {
    if (!draftSourceColumn) return {}
    for (const q of questions) {
      if (q.kind === 'group' && q.options.every((o) => o.sourceColumn === draftSourceColumn || q.options.length === 1)) {
        if (q.options.find((o) => o.sourceColumn === draftSourceColumn)) {
          return { sourceColumn: draftSourceColumn }
        }
      }
      if (q.kind === 'single' && q.options[0]?.sourceColumn === draftSourceColumn) {
        return { sourceColumn: draftSourceColumn }
      }
      if (q.kind === 'group') {
        const opt = q.options.find((o) => o.sourceColumn === draftSourceColumn)
        if (opt) return { sourceColumn: draftSourceColumn }
      }
    }
    return { sourceColumn: draftSourceColumn }
  }, [draftSourceColumn, questions])

  function handleSelect(sourceColumn: string) {
    onDraftSourceColumnChange(sourceColumn)
    onSourceDropdownOpenChange(false)
    onSourceSearchChange('')
    onDropdownClose?.()
  }

  if (readOnly && draftSourceColumn && onClear) {
    return (
      <div className="space-y-6">
        <div className="space-y-1.5">
          <p className="text-sm text-muted-foreground">{t('audienceCreate.sourceColumn')}</p>
          <div className="flex h-11 w-full items-center justify-between rounded-md border border-border bg-transparent px-3">
            <span className="truncate text-base text-foreground dark:text-white">
              {displayLabel}
            </span>
            <button
              type="button"
              onClick={onClear}
              disabled={isPatching}
              className="ml-2 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors disabled:opacity-50"
              aria-label={t('audienceCreate.clearSourceColumn')}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="space-y-1.5">
          <p className="text-sm text-muted-foreground">{t('audienceCreate.sourceColumn')}</p>
        <SegmentFilterQuestionPicker
          questions={questions}
          value={pickerValue}
          onSelectGroup={(question) => {
            handleSelect(question.options[0]?.sourceColumn ?? '')
          }}
          onSelectColumn={(col) => handleSelect(col)}
          placeholder={t('audienceCreate.selectSourceColumn')}
          searchPlaceholder={t('audienceCreate.searchColumns')}
          onSearchChange={onSourceSearchChange}
          disabled={isPatching || readOnly}
        />
      </div>

      {!hideButton ? (
        <div className="flex flex-col items-center justify-center gap-2 pt-5">
          {submitHint ? (
            <p className="max-w-md text-center text-sm text-muted-foreground">{submitHint}</p>
          ) : null}
          <Button
            type="button"
            variant={AUDIENCE_WIZARD_BUTTON_VARIANT}
            className={cn('h-10 rounded-md px-4 tracking-tight', AUDIENCE_WIZARD_BUTTON_CLASS)}
            onClick={onSubmit}
            disabled={isPatching || !canSubmit}
          >
            {isPatching ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('audienceCreate.mappingInProgress')}
              </>
            ) : (
              <>
                {resolvedSubmitLabel}
                <ChevronRight className="ml-1 h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      ) : null}
    </div>
  )
}
